import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Avatar,
  Typography,
  Chip,
  Stack,
  Rating,
  LinearProgress,
  Button,
  Divider,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  useTheme,
  useMediaQuery,
  alpha,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Close as CloseIcon,
  Star as StarIcon,
  ThumbUp as ThumbUpIcon,
  Inventory as InventoryIcon,
  CalendarToday as CalendarIcon,
  VerifiedUser as VerifiedIcon,
  ExpandMore as ExpandMoreIcon,
  PhotoCamera as PhotoCameraIcon,
  ArrowBackIosNew as PrevPhotoIcon,
  ArrowForwardIos as NextPhotoIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import api from '../../services/api';

/**
 * Modal réutilisable pour afficher la réputation et les avis d'un utilisateur
 * @param {boolean} open - État d'ouverture du modal
 * @param {function} onClose - Fonction de fermeture
 * @param {string} userId - ID de l'utilisateur dont on veut voir la réputation
 */
const UserReputationModal = ({ open, onClose, userId }) => {
  const { t, i18n } = useTranslation('reputation');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // États
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [reputationData, setReputationData] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 5,
    totalReviews: 0,
    totalPages: 0,
    hasMore: false
  });
  
  // États pour les accordions et photos
  const [expandedReview, setExpandedReview] = useState(null);
  const [reviewPhotos, setReviewPhotos] = useState({});
  const [loadingPhotos, setLoadingPhotos] = useState({});
  const [photoErrors, setPhotoErrors] = useState({});
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState({});

  // Charger les données de réputation
  useEffect(() => {
    if (open && userId) {
      loadReputationData();
      loadReviews(1);
    }
  }, [open, userId]);

  const loadReputationData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/users/${userId}/reputation`);
      setReputationData(response.data.data);
    } catch (err) {
      console.error('Error loading reputation:', err);
      setError(t('errorLoading', 'Erreur lors du chargement de la réputation'));
    } finally {
      setLoading(false);
    }
  };

  const loadReviews = async (page = 1) => {
    try {
      if (page === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setError(null);

      const response = await api.get(`/users/${userId}/reviews`, {
        params: { page, limit: 5, excludePhotos: true }
      });

      const { reviews: newReviews, pagination: newPagination } = response.data.data;

      if (page === 1) {
        setReviews(newReviews);
      } else {
        setReviews(prev => [...prev, ...newReviews]);
      }

      setPagination(newPagination);
    } catch (err) {
      console.error('Error loading reviews:', err);
      setError(t('errorLoadingReviews', 'Erreur lors du chargement des avis'));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (pagination.hasMore && !loadingMore) {
      loadReviews(pagination.page + 1);
    }
  };

  // Gérer l'expansion des accordions et charger les photos
  const handleAccordionChange = async (review) => {
    const reviewId = review?.id || review?._id;
    const isCurrentlyExpanded = expandedReview === reviewId;
    setExpandedReview(isCurrentlyExpanded ? null : reviewId);
    
    // Charger les photos quand on expand la review
    if (!isCurrentlyExpanded && !reviewPhotos[reviewId] && !loadingPhotos[reviewId]) {
      setLoadingPhotos((prev) => ({ ...prev, [reviewId]: true }));
      setPhotoErrors((prev) => ({ ...prev, [reviewId]: null }));
      
      try {        
        const response = await api.get(`/users/${userId}/reviews/${reviewId}/photos`, {
          params: { includeLinkedReservationPhotos: false }
        });
        const photos = response?.data?.data?.equipment_photos || response?.data?.equipment_photos || [];
        setReviewPhotos((prev) => ({ ...prev, [reviewId]: photos }));
        setCurrentPhotoIndex((prev) => ({ ...prev, [reviewId]: 0 }));
      } catch (err) {
        console.error('❌ [handleAccordionChange] Error loading photos:', err.response?.status, err.response?.data, err.message);
        setPhotoErrors((prev) => ({ ...prev, [reviewId]: `Erreur lors du chargement des photos (${err.response?.status || 'Erreur réseau'})` }));
      } finally {
        setLoadingPhotos((prev) => ({ ...prev, [reviewId]: false }));
      }
    }
  };

  const getReputationLevelColor = (level) => {
    const colors = {
      legendary: '#FFD700',
      excellent: '#4CAF50',
      good: '#2196F3',
      average: '#FF9800',
      new: '#9E9E9E'
    };
    return colors[level] || colors.new;
  };

  const getReputationLevelLabel = (level) => {
    const labels = {
      legendary: t('level.legendary', 'Légendaire'),
      excellent: t('level.excellent', 'Excellent'),
      good: t('level.good', 'Bon'),
      average: t('level.average', 'Moyen'),
      new: t('level.new', 'Nouveau')
    };
    return labels[level] || labels.new;
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          maxHeight: isMobile ? '100%' : '90vh'
        }
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${theme.palette.divider}`,
          py: 2
        }}
      >
        <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
          {t('title', 'Réputation')}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          size="small"
          sx={{
            color: theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ p: 0 }}>
        {loading && !reputationData ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : reputationData ? (
          <Box>
            {/* User Info Section */}
            <Box sx={{ p: 3, backgroundColor: alpha(theme.palette.primary.main, 0.03) }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  src={reputationData.user.profilePicture}
                  alt={reputationData.user.name}
                  sx={{ width: 80, height: 80 }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {reputationData.user.name}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Chip
                      label={getReputationLevelLabel(reputationData.reputation.level)}
                      size="small"
                      sx={{
                        backgroundColor: getReputationLevelColor(reputationData.reputation.level),
                        color: 'white',
                        fontWeight: 600
                      }}
                      icon={<VerifiedIcon sx={{ color: 'white !important' }} />}
                    />
                    <Typography variant="body2" color="text.secondary">
                      <CalendarIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                      {t('memberSince', 'Membre depuis {{date}}', {
                        date: format(new Date(reputationData.user.memberSince), 'dd/MM/yyyy')
                      })}
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </Box>

            <Divider />

            {/* Reputation Stats */}
            <Box sx={{ p: 3 }}>
              <Grid container spacing={3}>
                {/* Overall Rating */}
                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      borderRadius: 2
                    }}
                  >
                    <Stack spacing={1} alignItems="center">
                      <Typography variant="h2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                        {reputationData.reputation.score.toFixed(1)}
                      </Typography>
                      <Rating
                        value={reputationData.reputation.score}
                        precision={0.1}
                        readOnly
                        size="large"
                      />
                      <Typography variant="body2" color="text.secondary">
                        {t('basedOn', '{{count}} avis', {
                          count: reputationData.reputation.totalReviews
                        })}
                      </Typography>
                    </Stack>
                  </Paper>
                </Grid>

                {/* Stats Grid */}
                <Grid item xs={12} md={6}>
                  <Stack spacing={2}>
                    <Box>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                        <Typography variant="body2" color="text.secondary">
                          <ThumbUpIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                          {t('recommendationRate', 'Taux de recommandation')}
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {reputationData.reputation.recommendationRate.toFixed(0)}%
                        </Typography>
                      </Stack>
                    </Box>

                    <Box>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                        <Typography variant="body2" color="text.secondary">
                          <StarIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                          {t('totalReviews', 'Total des avis')}
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {reputationData.reputation.totalReviews}
                        </Typography>
                      </Stack>
                    </Box>

                    <Box>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                        <Typography variant="body2" color="text.secondary">
                          <InventoryIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                          {t('activeListings', 'Annonces actives')}
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {reputationData.reputation.activeListingsCount}
                        </Typography>
                      </Stack>
                    </Box>
                  </Stack>
                </Grid>

                {/* Rating Distribution */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    {t('ratingDistribution', 'Distribution des notes')}
                  </Typography>
                  <Stack spacing={1}>
                    {[5, 4, 3, 2, 1].map(star => {
                      const count = reputationData.reputation.ratingDistribution[star];
                      const percentage = reputationData.reputation.totalReviews > 0
                        ? (count / reputationData.reputation.totalReviews) * 100
                        : 0;

                      return (
                        <Stack key={star} direction="row" spacing={2} alignItems="center">
                          <Typography variant="body2" sx={{ minWidth: 60 }}>
                            {star} <StarIcon sx={{ fontSize: 14, verticalAlign: 'middle', color: 'warning.main' }} />
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={percentage}
                            sx={{
                              flex: 1,
                              height: 8,
                              borderRadius: 4,
                              backgroundColor: alpha(theme.palette.warning.main, 0.1),
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: theme.palette.warning.main
                              }
                            }}
                          />
                          <Typography variant="body2" sx={{ minWidth: 40, textAlign: 'right' }}>
                            {count}
                          </Typography>
                        </Stack>
                      );
                    })}
                  </Stack>
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* Reviews List */}
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                {t('reviews', 'Avis')} ({pagination.totalReviews})
              </Typography>

              {reviews.length === 0 ? (
                <Alert severity="info">
                  {t('noReviews', 'Aucun avis pour le moment')}
                </Alert>
              ) : (
                <Stack spacing={2}>
                  {reviews.map((review) => (
                    <Accordion
                      key={review.id}
                      expanded={expandedReview === review.id}
                      onChange={() => handleAccordionChange(review)}
                      elevation={0}
                      sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '8px !important',
                        '&:before': { display: 'none' },
                        '&.Mui-expanded': {
                          margin: 0,
                          marginBottom: 2
                        }
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          backgroundColor: alpha(theme.palette.primary.main, 0.02),
                          borderRadius: expandedReview === review.id ? '8px 8px 0 0' : '8px'
                        }}
                      >
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', pr: 2 }}>
                          <Avatar
                            src={review.reviewer?.profilePicture}
                            alt={review.reviewer?.name}
                            sx={{ width: 40, height: 40 }}
                          />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {review.reviewer?.name || t('anonymousUser', 'Utilisateur anonyme')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {format(new Date(review.created_at), 'dd/MM/yyyy')}
                            </Typography>
                          </Box>
                          <Rating value={review.rating} readOnly size="small" />
                          <Typography variant="body2" fontWeight={600} color="primary">
                            {review.rating}/5
                          </Typography>
                        </Stack>
                      </AccordionSummary>

                      <AccordionDetails sx={{ p: 3 }}>
                        <Stack spacing={3}>
                          {/* Commentaire */}
                          {review.comment && (
                            <Box>
                              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                  {t('comment')}
                              </Typography>
                              <Paper elevation={0} sx={{ p: 2, backgroundColor: 'grey.50' }}>
                                <Typography variant="body2">
                                  {review.comment}
                                </Typography>
                              </Paper>
                            </Box>
                          )}

                          {/* Recommandation */}
                          {review.would_recommend !== undefined && (
                            <Box>
                              <Chip
                                label={review.would_recommend ? t('recommends') : t('doesNotRecommend')}
                                size="small"
                                icon={<ThumbUpIcon />}
                                color={review.would_recommend ? 'success' : 'error'}
                                variant="outlined"
                              />
                            </Box>
                          )}

                          {/* Critères détaillés si disponibles */}
                          {review.criteria_scores && Object.keys(review.criteria_scores).length > 0 && (
                            <Box>
                              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                {t('detailedCriteria')}
                              </Typography>
                              <Stack spacing={1}>
                                {Object.entries(review.criteria_scores)
                                  .filter(([key]) => key !== 'would_recommend')
                                  .map(([key, scoreData]) => {
                                    const rating = typeof scoreData === 'number' ? scoreData : scoreData?.rating;
                                    const comment = typeof scoreData === 'object' ? scoreData?.comment : null;
                                    
                                    return (
                                      <Paper
                                        key={key}
                                        elevation={0}
                                        sx={{
                                          p: 1.5,
                                          backgroundColor: alpha(theme.palette.primary.main, 0.05),
                                          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                                        }}
                                      >
                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                          <Typography variant="body2" fontWeight={500}>
                                            {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                          </Typography>
                                          <Rating value={rating || 0} readOnly size="small" />
                                        </Stack>
                                        {comment && (
                                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                            {comment}
                                          </Typography>
                                        )}
                                      </Paper>
                                    );
                                  })}
                              </Stack>
                            </Box>
                          )}

                          {/* Section Photos */}
                          <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PhotoCameraIcon fontSize="small" />
                              {t('photos')}
                            </Typography>
                            {loadingPhotos[review.id] ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
                                <CircularProgress size={24} />
                                <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                                  {t('loadingPhotos')}
                                </Typography>
                              </Box>
                            ) : photoErrors[review.id] ? (
                              <Alert severity="warning">{photoErrors[review.id]}</Alert>
                            ) : reviewPhotos[review.id] && reviewPhotos[review.id].length > 0 ? (
                              <Box>
                                <Box sx={{ position: 'relative', width: '100%', height: 300, backgroundColor: 'grey.100', borderRadius: 2, overflow: 'hidden', mb: 2 }}>
                                  {/* Image principale */}
                                  <Box
                                    component="img"
                                    src={reviewPhotos[review.id][currentPhotoIndex[review.id] || 0]}
                                    alt={`Photo ${(currentPhotoIndex[review.id] || 0) + 1}`}
                                    sx={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'contain'
                                    }}
                                  />

                                  {/* Navigation si plusieurs photos */}
                                  {reviewPhotos[review.id].length > 1 && (
                                    <>
                                      <IconButton
                                        onClick={() => setCurrentPhotoIndex((prev) => ({
                                          ...prev,
                                          [review.id]: ((prev[review.id] || 0) - 1 + reviewPhotos[review.id].length) % reviewPhotos[review.id].length
                                        }))}
                                        sx={{
                                          position: 'absolute',
                                          left: 8,
                                          top: '50%',
                                          transform: 'translateY(-50%)',
                                          bgcolor: 'rgba(0,0,0,0.6)',
                                          color: 'white',
                                          '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' }
                                        }}
                                      >
                                        <PrevPhotoIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton
                                        onClick={() => setCurrentPhotoIndex((prev) => ({
                                          ...prev,
                                          [review.id]: ((prev[review.id] || 0) + 1) % reviewPhotos[review.id].length
                                        }))}
                                        sx={{
                                          position: 'absolute',
                                          right: 8,
                                          top: '50%',
                                          transform: 'translateY(-50%)',
                                          bgcolor: 'rgba(0,0,0,0.6)',
                                          color: 'white',
                                          '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' }
                                        }}
                                      >
                                        <NextPhotoIcon fontSize="small" />
                                      </IconButton>

                                      {/* Indicateur de position */}
                                      <Box
                                        sx={{
                                          position: 'absolute',
                                          bottom: 8,
                                          left: '50%',
                                          transform: 'translateX(-50%)',
                                          bgcolor: 'rgba(0,0,0,0.6)',
                                          color: 'white',
                                          px: 1.5,
                                          py: 0.5,
                                          borderRadius: 2,
                                          fontSize: '0.75rem'
                                        }}
                                      >
                                        {(currentPhotoIndex[review.id] || 0) + 1} / {reviewPhotos[review.id].length}
                                      </Box>
                                    </>
                                  )}
                                </Box>

                                {/* Miniatures */}
                                {reviewPhotos[review.id].length > 1 && (
                                  <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 1 }}>
                                    {reviewPhotos[review.id].map((photo, index) => (
                                      <Box
                                        key={index}
                                        component="img"
                                        src={photo}
                                        alt={`Miniature ${index + 1}`}
                                        onClick={() => setCurrentPhotoIndex((prev) => ({ ...prev, [review.id]: index }))}
                                        sx={{
                                          width: 60,
                                          height: 60,
                                          objectFit: 'cover',
                                          borderRadius: 1,
                                          cursor: 'pointer',
                                          border: (currentPhotoIndex[review.id] || 0) === index ? '2px solid' : '1px solid',
                                          borderColor: (currentPhotoIndex[review.id] || 0) === index ? 'primary.main' : 'divider',
                                          opacity: (currentPhotoIndex[review.id] || 0) === index ? 1 : 0.6,
                                          transition: 'all 0.2s',
                                          '&:hover': { opacity: 1 },
                                          flexShrink: 0
                                        }}
                                      />
                                    ))}
                                  </Stack>
                                )}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                {t('noAttachedPhotos')}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  ))}

                  {/* Load More Button */}
                  {pagination.hasMore && (
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={handleLoadMore}
                        disabled={loadingMore}
                        startIcon={loadingMore ? <CircularProgress size={20} /> : null}
                      >
                        {loadingMore
                          ? t('loading', 'Chargement...')
                          : t('loadMore', 'Afficher plus')}
                      </Button>
                      <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                        {t('showing', '{{current}} sur {{total}}', {
                          current: reviews.length,
                          total: pagination.totalReviews
                        })}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              )}
            </Box>
          </Box>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default UserReputationModal;
